import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmblaCarousel from "@tCooneyBrothers/Components/EmblaCarousel";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    industries = "industries",
    maximumIndustriesPerRow = "maximumIndustriesPerRow",
    maximumIndustryImageHeight = "maximumIndustryImageHeight",
}

interface IndustryFields {
    image: string;
    label: string;
}

interface IndustryItem extends HasFields {
    fields: IndustryFields;
}

interface Props extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.industries]: IndustryItem[];
        [fields.maximumIndustriesPerRow]: string;
        [fields.maximumIndustryImageHeight]: number;
    };
}

interface IndustryCarouselStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    imageFrame?: InjectableCss;
    image?: LazyImageProps;
    label?: TypographyProps;
}

const styles: IndustryCarouselStyles = {
    titleText: {
        variant: "h3",
        transform: "uppercase",
        css: css`
            text-align: left;
            margin: 0;
            padding: 30px 0;
            padding-left: 18px;
            font-size: clamp(24px, 3vw, 32px);
            font-weight: 700;
            line-height: 1.2;
            letter-spacing: 0;
        `,
    },
    imageContainer: {
        css: css`
            > div[class^="GridWrapper"] {
                > div[class^="GridOffset"] {
                    max-width: 100%;
                }
            }
        `,
    },
    imageItem: {
        css: css`
            justify-content: center;
            flex: 0 0 auto;
            padding: 0;
            position: relative;
            flex-direction: column;
        `,
    },
    imageFrame: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            aspect-ratio: 1;
            padding: 10px;
            box-sizing: border-box;
           

            > div {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                height: 100%;
                 border: 1px solid #d4d4d4;
            }

            img {
                display: block;
                max-width: 100%;
                max-height: 200px !important;
                object-fit: contain;
            }
        `,
    },
    image: {
        css: css`
            img {
                display: block;
                width: auto;
                max-width: 100%;
            }
        `,
    },
    label: {
        variant: "p",
        css: css`
            margin: 10px 0 0;
            color: #424242;
            font-size: 16px;
            font-weight: 700;
            line-height: 1.25;
            text-align: center;
        `,
    },
};

const IndustryImageCarousel: React.FC<Props> = ({ fields: content }) => {
    const configuredMaximum = Number.parseInt(content.maximumIndustriesPerRow || "5", 10);
    const maximumIndustriesPerRow = Number.isFinite(configuredMaximum)
        ? Math.min(6, Math.max(1, configuredMaximum))
        : 5;
    const tabletIndustriesPerRow = Math.ceil(maximumIndustriesPerRow / 2);
    const configuredImageHeight = Number(content.maximumIndustryImageHeight);
    const maximumImageHeight =
        Number.isFinite(configuredImageHeight) && configuredImageHeight > 0 ? configuredImageHeight : 220;
    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
            maxHeight: `${maximumImageHeight}px`,
        },
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="industryImageCarousel">
            {content.title && (
                <Typography variant="headerSecondary" {...styles.titleText} data-test-selector="industryImageCarouselTitle">
                    {content.title}
                </Typography>
            )}
            <FlexWrapContainer {...styles.imageContainer}>
                <EmblaCarousel>
                    {(content.industries || []).map((industry, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <FlexItem
                            key={index}
                            flexColumns={[
                                1,
                                tabletIndustriesPerRow,
                                tabletIndustriesPerRow,
                                maximumIndustriesPerRow,
                                maximumIndustriesPerRow,
                            ]}
                            {...styles.imageItem}
                        >
                            <StyledWrapper {...styles.imageFrame}>
                                <LazyImage
                                    src={industry.fields.image}
                                    altText={industry.fields.label}
                                    imgProps={imgProps}
                                    {...styles.image}
                                />
                            </StyledWrapper>
                            <Typography {...styles.label}>{industry.fields.label}</Typography>
                        </FlexItem>
                    ))}
                </EmblaCarousel>
            </FlexWrapContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: IndustryImageCarousel,
    definition: {
        group: "Common",
        displayName: "Industry Image Carousel",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "INDUSTRIES",
                fieldType: "General",
            },
            {
                name: fields.industries,
                displayName: "Industries",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay: (item: HasFields) => item.fields.label,
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Industry Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "label",
                        displayName: "Industry Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.maximumIndustriesPerRow,
                displayName: "Maximum industries per row",
                editorTemplate: "DropDownField",
                defaultValue: "5",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                ],
                fieldType: "General",
                tooltip:
                    "Sets the maximum number of industries visible per row on desktop. The carousel shows fewer items per row on smaller screens.",
                tooltipPosition: "left",
            },
            {
                name: fields.maximumIndustryImageHeight,
                displayName: "Maximum industry image height",
                editorTemplate: "IntegerField",
                defaultValue: 220,
                fieldType: "General",
                tooltip: "Limits the displayed height of industry images in pixels while preserving their proportions.",
            },
        ],
    },
};

export default widgetModule;
